/* BB section behaviours: carousels, the review wall and the sticky add to cart bar. */
const BB = window.BB;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
const designMode = Boolean(window.Shopify && window.Shopify.designMode);

/* Carousels: a scroll-snap track with arrows, a position indicator and mouse drag. Touch swiping is native. */
const carousels = new WeakSet();
const initCarousels = (root = document) => {
  $$('[data-bb-carousel]', root).forEach((carousel) => {
    const track = $('[data-bb-track]', carousel);
    if (!track || carousels.has(carousel)) return;
    carousels.add(carousel);
    const nav = $('[data-bb-nav]', carousel);
    const prev = nav ? $('[data-bb-prev]', nav) : null;
    const next = nav ? $('[data-bb-next]', nav) : null;
    const dots = nav ? $$('[data-bb-dot]', nav) : [];
    const step = () => {
      const [first, second] = track.children;
      return (first && second && second.offsetLeft - first.offsetLeft) || track.clientWidth;
    };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const scrollable = max > 2;
      if (nav) nav.hidden = !scrollable;
      if (!scrollable) return;
      const position = Math.abs(track.scrollLeft);
      const atEnd = position >= max - 2;
      if (prev) prev.disabled = position <= 2;
      if (next) next.disabled = atEnd;
      const index = atEnd ? dots.length - 1 : Math.min(dots.length - 1, Math.round(position / step()));
      dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
    };
    const move = (direction) => track.scrollBy({ left: direction * step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    if (prev) prev.addEventListener('click', () => move(-1));
    if (next) next.addEventListener('click', () => move(1));
    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(update);
      },
      { passive: true }
    );
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    update();

    let startX = 0;
    let startLeft = 0;
    let moved = false;
    const onMove = (event) => {
      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) < 6) return;
      moved = true;
      track.classList.add('is-dragging');
      track.scrollLeft = startLeft - delta;
    };
    const onEnd = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
      if (!moved) return;
      track.classList.remove('is-dragging');
      track.scrollTo({ left: Math.round(track.scrollLeft / step()) * step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    };
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || track.scrollWidth - track.clientWidth <= 2) return;
      if (event.target instanceof Element && event.target.closest('video[controls], iframe')) return;
      startX = event.clientX;
      startLeft = track.scrollLeft;
      moved = false;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onEnd);
      window.addEventListener('pointercancel', onEnd);
    });
    track.addEventListener('dragstart', (event) => event.preventDefault());
    track.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        moved = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  });
};

/* Review wall: filter chips and load more. Without JavaScript every review is shown. */
const initWalls = (root = document) => {
  $$('[data-bb-wall]', root).forEach((wall) => {
    if (wall.dataset.ready) return;
    wall.dataset.ready = 'true';
    const cards = $$('[data-bb-review]', wall);
    const more = $('[data-bb-wall-more]', wall);
    const status = $('[data-bb-wall-status]', wall);
    const empty = $('[data-bb-wall-empty]', wall);
    const initial = designMode ? cards.length : Number(wall.dataset.initial) || cards.length;
    const stepSize = Number(wall.dataset.step) || 6;
    let filter = 'all';
    let shown = initial;
    const matching = () => cards.filter((card) => (filter === 'photo' ? card.dataset.photo === 'true' : filter === 'five' ? card.dataset.stars === '5' : true));
    const render = () => {
      const list = matching();
      const visible = Math.min(shown, list.length);
      cards.forEach((card) => (card.hidden = true));
      list.slice(0, visible).forEach((card) => (card.hidden = false));
      if (more) more.hidden = visible >= list.length;
      if (status) status.textContent = list.length ? wall.dataset.status.replace('[shown]', String(visible)).replace('[total]', String(list.length)) : '';
      if (empty) empty.hidden = list.length > 0;
    };
    wall.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const chip = target.closest('[data-bb-wall-filter]');
      if (chip) {
        filter = chip.dataset.bbWallFilter;
        shown = initial;
        $$('[data-bb-wall-filter]', wall).forEach((button) => button.setAttribute('aria-pressed', String(button === chip)));
        render();
        return;
      }
      if (target.closest('[data-bb-wall-more]')) {
        const firstNew = matching()[shown];
        shown += stepSize;
        render();
        if (firstNew) {
          firstNew.setAttribute('tabindex', '-1');
          firstNew.focus({ preventScroll: true });
        }
      }
    });
    render();
  });
};

/* Sticky add to cart: shows once the buy box button has scrolled away, mirrors its state and submits its form. */
const initSticky = (root = document) => {
  $$('[data-bb-sticky]', root).forEach((bar) => {
    if (bar.dataset.ready) return;
    bar.dataset.ready = 'true';
    const product = $('bb-product');
    const form = $('[data-bb-sticky-form]', bar);
    const button = $('[data-bb-sticky-atc]', bar);
    const idInput = $('[data-bb-sticky-id]', bar);
    const setVisible = (visible) => {
      if (bar.hidden === !visible) return;
      bar.hidden = !visible;
      document.documentElement.style.setProperty('--bb-sticky-h', visible ? `${bar.offsetHeight}px` : '0px');
    };
    bar.bbShow = () => setVisible(true);
    const anchor = product ? $('[data-bb-atc]', product) || product : null;
    const after = Number(bar.dataset.after) || 600;
    let frame = 0;
    const check = () => setVisible(anchor ? anchor.getBoundingClientRect().bottom < 0 : window.scrollY > after);
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(check);
    };
    check();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    if (product) {
      product.addEventListener('bb:product:render', (event) => {
        const detail = event.detail;
        const variantNode = $('[data-bb-sticky-variant]', bar);
        const priceNode = $('[data-bb-sticky-price]', bar);
        if (variantNode) variantNode.textContent = detail.summary;
        if (priceNode) priceNode.textContent = detail.totalText;
        if (idInput) idInput.value = String(detail.id);
        if (button) {
          button.disabled = !detail.available;
          button.textContent = detail.available ? bar.dataset.addLabel : bar.dataset.soldOutLabel;
        }
      });
      if (product.variant && typeof product.render === 'function') product.render();
    }
    if (!form) return;
    form.addEventListener('submit', (event) => {
      if (product && product.form) {
        event.preventDefault();
        product.submitOpener = button;
        product.form.requestSubmit();
        return;
      }
      if (!BB || !BB.cart || !idInput) return;
      event.preventDefault();
      button.disabled = true;
      BB.cart
        .add([{ id: Number(idInput.value), quantity: 1 }], button)
        .catch(() => form.submit())
        .finally(() => (button.disabled = false));
    });
  });
};

const init = (root = document) => {
  initCarousels(root);
  initWalls(root);
  initSticky(root);
};

init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
document.addEventListener('shopify:section:select', (event) => {
  const bar = $('[data-bb-sticky]', event.target);
  if (bar && bar.bbShow) bar.bbShow();
});
document.addEventListener('shopify:block:select', (event) => {
  const item = event.target;
  const track = item instanceof Element ? item.closest('[data-bb-track]') : null;
  if (track && track.scrollWidth > track.clientWidth) track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: 'auto' });
});
